import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import MetricsCard from './components/MetricsCard';
import FilterToolbar from './components/FilterToolbar';
import ChartContainer from './components/ChartContainer';
import ExportPanel from './components/ExportPanel';
import AgentStatusPanel from './components/AgentStatusPanel';
import RealtimeUpdates from './components/RealtimeUpdates';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    dateRange: '30d',
    region: 'all',
    topic: 'all'
  });

  // Mock data for metrics
  const metricsData = [
    {
      title: "Total Responses",
      value: "24,847",
      change: "+12.5%",
      changeType: "positive",
      icon: "Users",
      color: "primary"
    },
    {
      title: "Active Agents",
      value: "3",
      change: "2 processing",
      changeType: "neutral",
      icon: "Bot",
      color: "success"
    },
    {
      title: "Data Points",
      value: "1.2M",
      change: "+8.3%",
      changeType: "positive",
      icon: "Database",
      color: "warning"
    },
    {
      title: "Insights Generated",
      value: "156",
      change: "+23.1%",
      changeType: "positive",
      icon: "Brain",
      color: "error"
    }
  ];

  // Mock data for charts
  const topThemesData = [
    { name: "Product Quality", value: 4200 },
    { name: "Customer Service", value: 3800 },
    { name: "Pricing", value: 3200 },
    { name: "Brand Trust", value: 2900 },
    { name: "User Experience", value: 2400 },
    { name: "Innovation", value: 2100 }
  ];

  const brandFrequencyData = [
    { name: "Apple", value: 35 },
    { name: "Samsung", value: 28 },
    { name: "Google", value: 22 },
    { name: "Microsoft", value: 15 }
  ];

  const sentimentTimelineData = [
    { name: "Jan", positive: 65, negative: 20, neutral: 15 },
    { name: "Feb", positive: 68, negative: 18, neutral: 14 },
    { name: "Mar", positive: 72, negative: 16, neutral: 12 },
    { name: "Apr", positive: 70, negative: 19, neutral: 11 },
    { name: "May", positive: 75, negative: 15, neutral: 10 },
    { name: "Jun", positive: 78, negative: 14, neutral: 8 },
    { name: "Jul", positive: 80, negative: 12, neutral: 8 },
    { name: "Aug", positive: 77, negative: 15, neutral: 8 },
    { name: "Sep", positive: 82, negative: 11, neutral: 7 },
    { name: "Oct", positive: 85, negative: 10, neutral: 5 }
  ];

  // Mock agent data
  const agentData = [
    {
      id: 1,
      name: "ProposalBot",
      icon: "FileText",
      status: "active",
      lastRun: "2 minutes ago",
      progress: 85,
      dataPoints: 12450
    },
    {
      id: 2,
      name: "InsightMiner",
      icon: "Search",
      status: "active",
      lastRun: "5 minutes ago",
      progress: 95,
      dataPoints: 8320
    },
    {
      id: 3,
      name: "Efficiency Auditor",
      icon: "BarChart3",
      status: "idle",
      lastRun: "1 hour ago",
      progress: null,
      dataPoints: 5680
    }
  ];

  const handleFiltersChange = (newFilters) => {
    setIsLoading(true);
    setFilters(newFilters);
    
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
    }, 1500);
  };

  const handleExport = async (type) => {
    console.log(`Exporting data as ${type}...`);
    // Simulate export process
    return new Promise(resolve => {
      setTimeout(resolve, 2000);
    });
  };

  const handleRefreshAgents = () => {
    console.log('Refreshing agent status...');
  };

  const handleNewRealtimeData = (update) => {
    console.log('New real-time update:', update);
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - Ipsos Insight Intelligence Platform</title>
        <meta name="description" content="Comprehensive data visualization and analysis dashboard for AI-driven market research insights" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Analytics Dashboard
                </h1>
                <p className="text-muted-foreground mb-4">
                  Real-time insights from your AI agents and market research data
                </p>
                
                {/* Launch Agent Button - Mobile centered */}
                <div className="flex justify-center">
                  <Button
                    variant="default"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => window.location.href = '/launch-agent'}
                    className="px-6"
                  >
                    Launch Agent
                  </Button>
                </div>
              </div>
            </div>

            {/* Metrics Cards with Refresh Button */}
            <div className="mb-8">
              {/* Refresh button positioned above Total Responses card */}
              <div className="flex justify-end mb-4">
                <Button
                  variant="outline"
                  iconName="RefreshCw"
                  iconPosition="left"
                  onClick={() => window.location?.reload()}
                  size="sm"
                >
                  Refresh All
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {metricsData?.map((metric, index) => (
                  <MetricsCard
                    key={index}
                    title={metric?.title}
                    value={metric?.value}
                    change={metric?.change}
                    changeType={metric?.changeType}
                    icon={metric?.icon}
                    color={metric?.color}
                  />
                ))}
              </div>
            </div>

            {/* Filter Toolbar */}
            <FilterToolbar 
              onFiltersChange={handleFiltersChange}
              isLoading={isLoading}
            />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              {/* Charts Section */}
              <div className="lg:col-span-2 space-y-6">
                <ChartContainer
                  type="bar"
                  data={topThemesData}
                  title="Top Discussion Themes"
                  description="Most frequently mentioned topics in survey responses"
                  height={350}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ChartContainer
                    type="pie"
                    data={brandFrequencyData}
                    title="Brand Mentions"
                    description="Distribution of brand references"
                    height={300}
                  />
                  
                  <div className="bg-card border border-border rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">
                      Quick Actions
                    </h3>
                    <div className="space-y-3">
                      <Button
                        variant="outline"
                        fullWidth
                        iconName="FileText"
                        iconPosition="left"
                        onClick={() => console.log('Generate report')}
                      >
                        Generate Report
                      </Button>
                      <Button
                        variant="outline"
                        fullWidth
                        iconName="Settings"
                        iconPosition="left"
                        onClick={() => window.location.href = '/settings-administration'}
                      >
                        Configure Alerts
                      </Button>
                      <Button
                        variant="outline"
                        fullWidth
                        iconName="Share"
                        iconPosition="left"
                        onClick={() => console.log('Share dashboard')}
                      >
                        Share Dashboard
                      </Button>
                    </div>
                  </div>
                </div>

                <ChartContainer
                  type="line"
                  data={sentimentTimelineData}
                  title="Sentiment Timeline"
                  description="Sentiment analysis trends over the past 10 months"
                  height={350}
                />
              </div>

              {/* Side Panel */}
              <div className="space-y-6">
                <AgentStatusPanel
                  agents={agentData}
                  onRefresh={handleRefreshAgents}
                  isRefreshing={false}
                />
                
                <ExportPanel onExport={handleExport} />
                
                <RealtimeUpdates onNewData={handleNewRealtimeData} />
              </div>
            </div>

            {/* Additional Insights Section */}
            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="Lightbulb" size={20} className="text-warning" />
                <h3 className="text-lg font-semibold text-foreground">
                  AI-Generated Insights
                </h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="TrendingUp" size={16} className="text-success mt-1" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">
                        Positive Trend Detected
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Customer satisfaction scores have increased by 15% this quarter, 
                        particularly in the product quality category.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning mt-1" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">
                        Attention Required
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Pricing concerns mentioned 23% more frequently compared to last month. 
                        Consider reviewing pricing strategy.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 bg-muted/30 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Icon name="Target" size={16} className="text-primary mt-1" />
                    <div>
                      <h4 className="text-sm font-medium text-foreground mb-1">
                        Opportunity Identified
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        Innovation-related discussions show 40% positive sentiment. 
                        Strong opportunity for new product launches.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;